import { http } from '@/api/http';
import type { OperationResult, Schemas } from '@/api/shared';

/**
 * Live PhenoTips wire shape for `/persons/{guid}/genetic-info`
 * (`GeneticInfoDTO` in the backend OpenAPI). GET/POST/PUT all speak this
 * exact object — camelCase keys, string enums with an `"na"` unset
 * sentinel, and a `{day, month, year}` triple for `dateObserved`.
 *
 * The FE's *generated* `Variant` schema (`api/schema.ts`) is an older BFF
 * shape (`gene` / `variant` / `acmg_criteria`); it does NOT match this
 * endpoint. This interface — hand-maintained against the OpenAPI source
 * (PTV2-1172) — is the source of truth for reads and writes. `mapGeneticVariant`
 * narrows it to the display view model.
 *
 * Enum fields are typed as `string` (validation is the backend's job — it
 * returns 422 for unknown values). The valid value sets live in
 * `data/geneticFieldOptions.ts` so the form only ever offers legal codes.
 *
 * PUT is a full replace: the server overwrites the row with the body sent,
 * so the edit form reconstructs the whole object from the original DTO plus
 * the user's edits (see `variantForm.draftToDto`).
 */
export interface LiveVariantPartialDate {
  readonly day: number;
  readonly month: number;
  readonly year: number;
}

export interface LiveVariantSource {
  readonly type: string;
  readonly label?: string;
}

export interface GeneticInfoDto {
  readonly id?: string;
  readonly geneId?: string;
  readonly geneSymbol?: string;
  /** na | snv | indel | cnv | mitochondrial | structural | repeat_expansion */
  readonly variantType?: string;
  /** GRCh37 | GRCh38 | GRCh39 | NCBI36 */
  readonly referenceGenome?: string;
  readonly orderRefs?: readonly string[];
  /** na | 1..22 | X | Y | MT */
  readonly chromosome?: string;
  readonly start?: string;
  readonly end?: string;
  readonly transcript?: string;
  readonly protein?: string;
  readonly cdna?: string;
  readonly interpretation?: string;
  /** candidate | rejected_candidate | confirmed_casual | carrier | tested_negative */
  readonly status?: string;
  readonly refAllele?: string;
  readonly altAllele?: string;
  /** na | heterozygous | homozygous | hemizygous */
  readonly zygositySnv?: string;
  /** na | deletion | duplication | amplification | loss | gain */
  readonly cnvType?: string;
  /** unclassified | copy_number_polymorphism | clinically_significant */
  readonly cnvClassification?: string;
  /** na | whole_gene | partial_gene | multi_gene | intergenic */
  readonly cnvStructuralImpact?: string;
  /** na | loss_of_function | gain_of_function | dosage_sensitivity | benign_polymorphic_gain | uncertain_significance */
  readonly cnvFunctionalImpact?: string;
  readonly cytoband?: string;
  readonly copyNumber?: number;
  readonly mtdna?: string;
  /** na | homoplasmic | heteroplasmic | mosaic */
  readonly zygosityMtdna?: string;
  readonly heteroplasmyPercent?: number;
  /** na | deletion | duplication | inversion | translocation */
  readonly structuralType?: string;
  readonly breakpoints?: readonly string[];
  readonly chromosomeBreakpoint?: string;
  readonly positionBreakpoint?: string;
  readonly fusion?: string;
  readonly repeatUnit?: string;
  readonly repeatCount?: number;
  readonly dbsnp?: string;
  /** na | de_novo_germline | de_novo_s_mosaicism | maternal | paternal | biparental | unknown */
  readonly inheritance?: string;
  /** na | positive | negative */
  readonly sangerValidation?: string;
  /** na | segregates | does_not_segregates */
  readonly segregation?: string;
  readonly comments?: string;
  readonly effects?: readonly string[];
  readonly strategies?: readonly string[];
  readonly evidences?: readonly string[];
  readonly dateObserved?: LiveVariantPartialDate | null;
  readonly source?: LiveVariantSource;
  readonly lock_version?: number;
}

export const variantsApi = {
  /** GET /persons/{guid}/genetic-info — genetic variants for a patient. */
  getGeneticVariants: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getVariants'>> =>
    http.get<OperationResult<'getVariants'>>(
      `/persons/${encodeURIComponent(guid)}/genetic-info`,
      options,
    ),

  /**
   * POST /persons/{guid}/genetic-info — add a new variant. Body is the full
   * {@link GeneticInfoDto}; the list GET returns the same shape.
   */
  createVariant: (
    guid: string,
    body: GeneticInfoDto,
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['Variant']> =>
    http.post<Schemas['Variant']>(
      `/persons/${encodeURIComponent(guid)}/genetic-info`,
      body,
      options,
    ),

  /**
   * PUT /persons/{personGuid}/genetic-info/{id} — full-replace update of a
   * single variant. Body is the complete {@link GeneticInfoDto}.
   */
  updateVariant: (
    personGuid: string,
    id: string,
    body: GeneticInfoDto,
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['Variant']> =>
    http.put<Schemas['Variant']>(
      `/persons/${encodeURIComponent(personGuid)}/genetic-info/${encodeURIComponent(id)}`,
      body,
      options,
    ),

  /** DELETE /persons/{personGuid}/genetic-info/{id} — remove a single variant. */
  deleteVariant: (
    personGuid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(personGuid)}/genetic-info/${encodeURIComponent(id)}`,
      options,
    ),
};
